import type { PlanSlot } from '@ebalance/proto/bffuser/v1/procurement_plan_model_pb'

import {
  type SlotValue,
  calcColumnTotals,
  calcSupplyDemandBalance,
  rowToSlots,
  toSlotValue,
} from '~/pages/ProcurementPlanDetail/models/calc'
import {
  type BreakdownColumn,
  type BreakdownSourceRow,
  type ColumnSide,
  buildBreakdownColumns,
  mergeSlots,
  resolveColumnKey,
} from '~/pages/ProcurementPlanDetail/models/columns'

/**
 * 詳細画面 (read-only) の 48 コマ表を組み立てる純関数群。版の詳細 (`index.tsx`) と
 * eBalance に提出記録の無い計画の詳細 (`NoSubmissionRecord.tsx`、AC-PPD-028) が同じ
 * 列規則・合計規則を使うためにここに置く。
 */

/** 計画値行 (版の値行・不整合通知の行) の共通形。 */
export type DetailValueRow = BreakdownSourceRow & {
  slots: PlanSlot[]
}

/** proto PlanSlot 配列（48 要素）を SlotValue[48] に変換する (値の無いコマは undefined のまま)。 */
export function slotsToValues(
  slots: ReadonlyArray<{ valueKwh?: bigint }>,
): SlotValue[] {
  const result = Array<SlotValue>(48).fill(undefined)
  for (let i = 0; i < Math.min(slots.length, 48); i++) {
    result[i] = toSlotValue(slots[i]?.valueKwh)
  }
  return result
}

/**
 * 計画値行を列キー（resolveColumnKey）でまとめたスロットマップを構築する。
 * JEPX 行は固定列キーに、同一列キーに複数行が来たら mergeSlots で合算する。
 */
export function buildSlotsMap(
  rows: readonly DetailValueRow[],
  side: ColumnSide,
): Map<string, SlotValue[]> {
  const m = new Map<string, SlotValue[]>()
  for (const row of rows) {
    const key = resolveColumnKey(row, side)
    m.set(key, mergeSlots(m.get(key), rowToSlots(row)))
  }
  return m
}

/**
 * 合計（調達計 / 販売計）の入力集合: 表示列のうち「空でない列」（全コマ未入力でない列）
 * のスロット配列。
 *
 * - **表示列（procurementColumns）を入力源にする**ことで、沖縄など非表示の列は集計にも
 *   含まれず、「調達計 = 表示している調達列の和」を常に保証する。
 * - 内訳列は取引なしコマを 0 とする（columns.ts emptySlots / calc.ts rowToSlots）ため、
 *   生成済み計画では通常すべての列が「空でない（0 含む）」= 全列が合算対象になり、相対契約分の
 *   ような取引なし列も 0 として合算される（合計値は 0 なので不変）。本フィルタは全コマ未設定の
 *   列が万一あった場合に集計から除外する防御として残す。
 * - canonical な集計は backend の提出時集計（proto は totals を持たず frontend が算出）。
 */
function nonEmptyColumnSlots(columnSlots: SlotValue[][]): SlotValue[][] {
  return columnSlots.filter((slots) => slots.some((v) => v !== undefined))
}

/**
 * 詳細画面（read-only の生成済み計画）の合計（調達計 / 販売計）を算出する。
 *
 * 取引先列が 1 つも無い内訳（相対契約を持たない構成小売など、生成 worker が行を出さない）
 * は「取引なし = 0」なので合計は 0 とする（"−" にしない）。`calcSlotTotal` は空列集合を
 * undefined とするが、それは編集・提出フローの「未入力を 0 に潰さない」セマンティクス
 * （`calc.ts` 鉄則）であり、read-only 詳細画面には当たらない — 詳細画面は `rowToSlots` が
 * 取引なしコマを 0 に倒すのと同じ「取引なし = 0」原則で、spec AC-PPD-006-1（未登録内訳は
 * 0 扱いで合計・需給バランスを算出）に従う。列が 1 つでもあれば通常どおり合算する。
 */
export function detailColumnTotals(columns: BreakdownColumn[]): SlotValue[] {
  return detailSlotsTotals(columns.map((c) => c.slots))
}

function detailSlotsTotals(columnSlots: SlotValue[][]): SlotValue[] {
  const nonEmpty = nonEmptyColumnSlots(columnSlots)
  if (nonEmpty.length === 0) {
    return Array<SlotValue>(48).fill(0)
  }
  return calcColumnTotals(nonEmpty)
}

/**
 * 前回提出の需給バランス 48 コマ (AC-PPD-006 の式を前回値に当てたもの)。
 *
 * 調達・販売の合計は、今回の合計 ([[detailColumnTotals]]) と同じ合計規則で前回提出の
 * 全列 (今回の表示列ではない) から出す。前回提出に無かった列は合計に入らないので前回値 0 と
 * 同じになる (AC-PLC-013)。前回の需要計画値が無いコマは前回の需給バランスも undefined に
 * し、差分にしない (前回値が無ければ変わったとしない。mock-app `PlanSubmissionPeriodMath.IsChanged`)。
 */
export function detailPreviousBalances(
  prevProcurementMap: ReadonlyMap<string, SlotValue[]>,
  prevDemandSlots: readonly SlotValue[],
  prevSalesMap: ReadonlyMap<string, SlotValue[]>,
): SlotValue[] {
  const procurementTotals = detailSlotsTotals([...prevProcurementMap.values()])
  const salesTotals = detailSlotsTotals([...prevSalesMap.values()])
  return Array.from({ length: 48 }, (_, i) =>
    calcSupplyDemandBalance(
      procurementTotals[i],
      prevDemandSlots[i],
      salesTotals[i],
    ),
  )
}

/** `PlanTable` に渡す、前回差分を持たない表の値一式。 */
export type ReadOnlyTableView = {
  demandSlots: SlotValue[]
  procurementColumns: BreakdownColumn[]
  salesColumns: BreakdownColumn[]
  procurementTotals: SlotValue[]
  salesTotals: SlotValue[]
  balances: SlotValue[]
}

/** 通知の行の同じ列への合算。どちらかに値の無いコマは undefined (0 に倒さない)。 */
function mergeNoticeSlots(
  into: SlotValue[] | undefined,
  add: SlotValue[],
): SlotValue[] {
  if (into === undefined) return [...add]
  return into.map((v, i) => {
    const w = add[i]
    return v === undefined || w === undefined ? undefined : v + w
  })
}

/** 通知の行を列キーでまとめる。値の無いコマは undefined のまま持つ。 */
function buildNoticeSlotsMap(
  rows: readonly DetailValueRow[],
  side: ColumnSide,
): Map<string, SlotValue[]> {
  const m = new Map<string, SlotValue[]>()
  for (const row of rows) {
    const key = resolveColumnKey(row, side)
    m.set(key, mergeNoticeSlots(m.get(key), slotsToValues(row.slots)))
  }
  return m
}

/** 通知の列の合計。列のどれかに値の無いコマは undefined。列が無ければ取引なしとして 0。 */
function noticeColumnTotals(columns: BreakdownColumn[]): SlotValue[] {
  if (columns.length === 0) return Array<SlotValue>(48).fill(0)
  return calcColumnTotals(columns.map((c) => c.slots))
}

/**
 * eBalance に提出記録の無い計画 (AC-PPD-028) の表を不整合通知の行から組み立てる。
 *
 * - 列: 版の詳細と同じ列規則 (`source_type` / `jepx_spot_portion` → `resolveColumnKey`) に流す。
 *   同じ取引先コードの行 (1 取引先に複数の相対契約) は同じ列キーになり 1 列に合算される
 *   (表示は「取引先毎合計」だけ)
 * - 値の無いコマ: 通知に値が無いコマは版の詳細のように 0 (取引なし) に倒さず undefined の
 *   まま流す。同じ列に合算する行のどれかに値が無いコマは列も「−」、列のどれかが「−」の
 *   コマは調達計・販売計も「−」(AC-PPD-006-1 と同じ表記、`calcColumnTotals`)
 * - 調達計・販売計: 通知の取引先別の値の合計。列が 1 つも無い側は取引なしとして 0
 * - 需要計画値: 通知は需要を保持しないので全コマ undefined (「−」表記)。需給バランスは
 *   需要が undefined なので全コマ undefined になる (AC-PPD-006-1 と同じ表記)
 */
export function buildNoSubmissionRecordTable(params: {
  procurementRows: readonly DetailValueRow[]
  salesRows: readonly DetailValueRow[]
  translate: (key: string) => string
}): ReadOnlyTableView {
  const { procurementRows, salesRows, translate } = params
  const buildColumns = (
    rows: readonly DetailValueRow[],
    side: ColumnSide,
  ): BreakdownColumn[] =>
    buildBreakdownColumns({
      rows: [...rows],
      side,
      slotsMap: buildNoticeSlotsMap(rows, side),
      prevMap: new Map(),
      hasPrev: false,
      translate,
    })
  const procurementColumns = buildColumns(procurementRows, 'procurement')
  const salesColumns = buildColumns(salesRows, 'sales')
  const procurementTotals = noticeColumnTotals(procurementColumns)
  const salesTotals = noticeColumnTotals(salesColumns)
  const demandSlots = Array<SlotValue>(48).fill(undefined)
  const balances = Array.from({ length: 48 }, (_, i) =>
    calcSupplyDemandBalance(
      procurementTotals[i],
      demandSlots[i],
      salesTotals[i],
    ),
  )
  return {
    demandSlots,
    procurementColumns,
    salesColumns,
    procurementTotals,
    salesTotals,
    balances,
  }
}
